import { applyTokens } from "../../../utilities/applyTokens.js";
import { createFocusStyle } from "../utils/createFocusStyle.js";
const iconSize = (width, height) => ({
    fontSize: height.value,
    lineHeight: height.value,
    width: width.value,
    height: width.value,
});
const inheritSize = {
    "&>*:nth-of-type(1)": {
        width: "inherit",
        height: "inherit",
        fontSize: "inherit",
        lineHeight: "inherit",
    },
};
const createStates = (tokens, type, destructive = false) => {
    const button = destructive ? tokens.component.button.destructive : tokens.component.button;
    const { default: defaultState, hover, active, disabled } = button[type];
    return Object.assign(Object.assign({}, applyTokens(defaultState)), { "--padding-offset": "borderWidth" in defaultState ? defaultState.borderWidth.value : "0px", "&:not(.Mui-disabled):hover": Object.assign({}, applyTokens(hover)), "&:not(.Mui-disabled):active": Object.assign({}, applyTokens(active)), "&.Mui-disabled": Object.assign({}, applyTokens(disabled)) });
};
const createOffsetPadding = (padding) => `calc(${padding} - var(--padding-offset, 0px))`;
const createSize = (tokens, size) => {
    var _a;
    const { component: { button }, semantic: { iconSize: iconSizes }, } = tokens;
    const buttonSize = button[size];
    const { icon: { width, height }, } = buttonSize;
    // find the same token in `iconSizes`
    const iconSizeKey = (_a = Object.entries(iconSizes).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, value]) => value.value === width.value)) === null || _a === void 0 ? void 0 : _a[0];
    return Object.assign(Object.assign(Object.assign({}, applyTokens(buttonSize)), { paddingLeft: createOffsetPadding(buttonSize.paddingLeft.value), paddingRight: createOffsetPadding(buttonSize.paddingRight.value), "& > svg:only-of-type": Object.assign({ 
            // Prevent the icon from being squished
            flexShrink: 0 }, iconSize(width, height)), "&:has(> svg:only-of-type)": Object.assign({}, applyTokens(button.iconOnly[size])), ".MuiCircularProgress-root": {
            // MUI sets an inline style for the circular progress, so we need to use !important
            width: `${width.value} !important`,
            height: `${height.value} !important`,
        } }), (iconSizeKey && {
        "--atlas-icon-size": iconSizeKey,
    }));
};
const button = (tokens) => {
    const { component: { button }, } = tokens;
    const { small, medium, large, withLeadingIcon, withTrailingIcon, iconOnly } = button;
    return {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    "&:focus": createFocusStyle(tokens),
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    minWidth: "unset",
                    ".MuiLoadingButton-loadingIndicatorStart": {
                        left: withLeadingIcon.paddingLeft.value,
                    },
                    ".MuiLoadingButton-loadingIndicatorEnd": {
                        right: withTrailingIcon.paddingRight.value,
                    },
                },
                containedPrimary: createStates(tokens, "primary"),
                outlinedPrimary: createStates(tokens, "secondary"),
                textPrimary: createStates(tokens, "tertiary"),
                containedDestructive: createStates(tokens, "primary", true),
                outlinedDestructive: createStates(tokens, "secondary", true),
                textDestructive: createStates(tokens, "tertiary", true),
                sizeSmall: createSize(tokens, "small"),
                sizeMedium: createSize(tokens, "medium"),
                sizeLarge: createSize(tokens, "large"),
                startIcon: Object.assign({ marginRight: withLeadingIcon.icon.marginRight.value, marginLeft: `calc(-1 *(${medium.paddingLeft.value} - ${withLeadingIcon.paddingLeft.value}))` }, inheritSize),
                endIcon: Object.assign({ marginLeft: withTrailingIcon.icon.marginLeft.value, marginRight: `calc(-1 *(${medium.paddingRight.value} - ${withTrailingIcon.paddingRight.value}))` }, inheritSize),
                iconSizeSmall: iconSize(small.icon.width, small.icon.height),
                iconSizeMedium: iconSize(medium.icon.width, medium.icon.height),
                iconSizeLarge: iconSize(large.icon.width, large.icon.height),
            },
        },
        MuiIconButton: {
            defaultProps: {
                color: "tertiary",
            },
            styleOverrides: {
                sizeSmall: Object.assign(Object.assign(Object.assign({}, applyTokens(small)), applyTokens(iconOnly.small)), { "> svg": Object.assign({}, iconSize(small.icon.width, small.icon.height)) }),
                sizeMedium: Object.assign(Object.assign(Object.assign({}, applyTokens(medium)), applyTokens(iconOnly.medium)), { "> svg": Object.assign({}, iconSize(medium.icon.width, medium.icon.height)) }),
                sizeLarge: Object.assign(Object.assign(Object.assign({}, applyTokens(large)), applyTokens(iconOnly.large)), { "> svg": Object.assign({}, iconSize(large.icon.width, large.icon.height)) }),
                colorPrimary: createStates(tokens, "primary"),
                colorSecondary: createStates(tokens, "secondary"),
                colorTertiary: createStates(tokens, "tertiary"),
                colorInherit: createStates(tokens, "tertiary"),
                colorPrimaryDestructive: createStates(tokens, "primary", true),
                colorSecondaryDestructive: createStates(tokens, "secondary", true),
                colorTertiaryDestructive: createStates(tokens, "tertiary", true),
            },
        },
    };
};
export default button;
//# sourceMappingURL=button.js.map